import React, { useRef, useState } from 'react';
import { Props as FormProps } from '../ActionForm';
import { type ClientFlowActionLfaCreateRequest } from '~/graphql/types.client';
import { Body } from '~/components/atom/Typography';
import InputLabel from '~/components/atom/InputLabel';
import TextButton from '~/components/atom/TextButton';
import Selector from '../Selector';
import { FlowAction } from '~/graphql/types';
import useBuilderContext from '../../../Builder/hooks/useBuilderContext';
import useRelativeMaps from '../../../Builder/hooks/useRelativeMaps';
import JustificationContainer from '~/components/atom/JustificationContainer';
import PositionWrapper from '../ConditionEditorV2/components/PositionWrapperV2';
import type { FlowPath } from '../Selector/utils/getFieldsByPath';
import isPointerArgument from '../../../../util/isPointerArgument';
import getPathForPointer from '../Selector/utils/getPathForPointer';
import { getPathRepresentation } from '../ConditionEditorV2/utils/getLabelForRep';
import ActionLabelAsText from '../../../Builder/components/ActionLabelAsText';
import EditableBlockWithIcon from '~/components/atom/EditableBlockWithIcon';
import PluginsEditor from '~/components/organism/PluginsEditor';
import TextEditorContainer from '~/components/atom/TextEditorContainer';
import ELEMENTS from '~/components/organism/PluginsEditor/components/elements/elementsEnum';
import useDHEditorInFlowBuilder from '../../hooks/useDHEditorInFlowBuilder';
import insertVariable from '~/components/organism/PluginsEditor/commands/modify/variable';
import NoSpaceTextButton from '~/components/atom/NoSpaceTextButton';
import styled, { css } from 'styled-components';

const text = {
  emptyValue: 'Selecteer een waarde',
  description: 'Voeg een notitie en een event toe',
  triggeringEvent: 'Trigger event',
};

export type Props = FormProps & {
  dataTestId?: string;
  action: ClientFlowActionLfaCreateRequest;
};

const LfaCreateRequest: React.FCC<Props> = ({ action, onChange }) => {
  const [showSelector, setShowSelector] = useState(false);

  const parentRef = useRef<null | HTMLButtonElement>(null);
  const maps = useRelativeMaps({ actionId: action.id });

  const { opts, primitiveInput, primitiveListInput, superSubjects } =
    useBuilderContext();

  const hasValue = action.event != null;

  let selectorPath: FlowPath = [];
  let variableName: string | null = null;
  if (action.event && isPointerArgument(action.event)) {
    const path = getPathForPointer(action.event.pointer, {
      subjectMap: maps.subjectMap,
      instanceMap: maps.instanceMap,
      directoryMap: maps.directoryMap,
    });

    if (path.error === undefined) {
      selectorPath = path.result;
      variableName = getPathRepresentation(path.result);
    }
  }

  const { value: bodyValue, onChange: onBodyChange } = useDHEditorInFlowBuilder(
    {
      actionId: action.id,
      html: action.note?.template || '<div></div>',
      mappings: Array.isArray(action.note?.mappings)
        ? action.note.mappings
        : [],
      customElements: [ELEMENTS.VARIABLE],
      updaterFn: updatedValue => {
        onChange({
          ...action,
          noteValue: updatedValue,
        });
      },
    },
  );

  return (
    <JustificationContainer direction="column" gap="m">
      <Body margin={[null]}>{text.description}</Body>

      <JustificationContainer width="100%" direction="column">
        <InputLabel label="Notitie" />
        <TextEditorContainer style={{ position: 'relative' }}>
          <PluginsEditor
            key={10000}
            customElements={[ELEMENTS.VARIABLE]}
            value={bodyValue}
            onChange={onBodyChange}
            minHeight={45}
            dataTestId="lfa-create-request-body-field"
            hideToolbar
          >
            {/* @ts-ignore cant get children typed correctly */}
            {editor =>
              editor && (
                <ButtonContainer>
                  <NoSpaceTextButton
                    appearance="primary"
                    icon="user"
                    onClick={() => insertVariable(editor)}
                  />
                </ButtonContainer>
              )
            }
          </PluginsEditor>
        </TextEditorContainer>
      </JustificationContainer>

      <JustificationContainer direction="column" width="100%" gap="xxs">
        <InputLabel label={text.triggeringEvent} />
        {hasValue ? (
          <EditableBlockWithIcon
            onClick={e => {
              e.stopPropagation();
              setShowSelector(true);
            }}
            icon="zap"
            active={false}
            ref={parentRef}
            hasError={variableName === null}
          >
            {variableName ? (
              <ActionLabelAsText str={variableName} />
            ) : (
              text.emptyValue
            )}
          </EditableBlockWithIcon>
        ) : (
          <TextButton
            dataTestId={'TEST_ID.TEXT_BUTTON'}
            label={'text.selectAValue'}
            padding={[null]}
            onClick={e => {
              e.stopPropagation();
              setShowSelector(true);
            }}
            ref={parentRef}
          />
        )}

        {showSelector && (
          <PositionWrapper
            onClose={() => setShowSelector(false)}
            parentRef={parentRef.current}
          >
            <Selector
              onClose={() => setShowSelector(false)}
              onSelect={value => {
                if (
                  value.__typename === 'Flow___Argument_File' ||
                  value.__typename === 'Flow___InstanceCondition' ||
                  value.__typename === 'Flow___SubjectFieldCondition'
                ) {
                  return;
                }

                onChange({
                  ...action,
                  event: value,
                });
                setShowSelector(false);
              }}
              inputPrimitives={primitiveInput}
              inputListPrimitives={primitiveListInput}
              argument={undefined}
              initialPath={selectorPath}
              opts={{
                ...opts,
                ...maps,
                action: FlowAction.LfaCreateRequest,
                conditionType: 'condition',
                limitToInstanceSubjects:
                  superSubjects['Event']?.subjectIds ?? [],
              }}
            />
          </PositionWrapper>
        )}
      </JustificationContainer>
    </JustificationContainer>
  );
};

const ButtonContainer = styled.div(
  ({ theme }) => css`
    position: absolute;
    right: ${theme.space('xs')};
    top: 50%;
    transform: translateY(-50%);
    margin-right: ${theme.space('xs')};
  `,
);

export default LfaCreateRequest;
